import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import styled, { useTheme } from 'styled-components/macro';
import { useOnClickOutside } from 'lib/utils';
import { LoadingIndicator, Modal } from 'lib/components';
import { sendXLSXAnalyticsReport } from 'lib/api';
import { StatusOptionsValues } from 'app/pages/video/videoDetails/components/const';
import { successToast } from 'lib/components/toasts/success';
import { RecipientsSelector } from '../../components/RecipientsSelector';
import { MdInfo } from 'react-icons/md';
import { useAuth } from 'lib/context';
import { useToastError } from 'lib/hooks';
import { Button } from 'react-covideo-common';
import { IoMdClose } from 'react-icons/io';
import { getSecondaryColor } from 'lib/context/theme/utils';

const Header = styled.div`
  display: flex;
`;

const HeaderTitle = styled.div`
  font-weight: 700;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.black[100]};
`;

const CloseButtonWrap = styled.div`
  margin-left: auto;
  color: ${({ theme }) => theme.colors.white[100]};
  height: 24px;
  width: 24px;
  &:hover {
    opacity: 0.7;
    cursor: pointer;
  }
`;

const Content = styled.div`
  width: 500px;
  display: flex;
  flex-direction: column;
  margin-top: 16px;

  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  color: ${({ theme }) => theme.colors.black[100]};
`;

const Footer = styled.div`
  margin-top: 32px;
  display: flex;
  justify-content: flex-end;
`;

const ErrorMessage = styled.div`
  height: 20px;
  font-size: 14px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  color: ${({ theme }) => theme.colors.danger[100]};
  padding-top: 4px;
`;

const Caption = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 8px;
  margin-top: 14px;
`;

const InfoMsgWrapper = styled.div`
  width: 95%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 5px;
  background: ${({ theme }) => theme.colors.info[20]};
  border-radius: 5px;
  border: 1px solid ${({ theme }) => getSecondaryColor(theme, 60)};
  padding: 12px;
`;

const Paragraph = styled.p`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => getSecondaryColor(theme, 60)};
  margin: 0;
`;

type Props = {
  handleModalClose: (modals: null) => void;
  params?:
    | {
        search: string;
        emailStatus: StatusOptionsValues;
        SMS: number;
      }
    | {};
  videoId?: string;
  data?: any;
  widgets?: any;
};

export const SendReportModal = (props: Props) => {
  const { t } = useTranslation();
  const themes = useTheme();
  const [recipients, setRecipients] = useState([] as string[]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { handleModalClose } = props;
  const { userData } = useAuth();
  const { ref } = useOnClickOutside(handleModalClose);
  const { showError } = useToastError();

  const escPress = (key: string) => {
    if (key === 'Escape') {
      handleModalClose(null);
    }
  };
  React.useEffect(() => {
    document.addEventListener('keyup', e => escPress(e.key));

    return () =>
      document.removeEventListener('keyup', e => escPress(e.key), true);
  }, []);

  const sendReport = async () => {
    setLoading(true);

    try {
      let widgetsExportData = props.data.filter(
        (data: any) =>
          props.widgets.find((widget: any) => data.title === widget.label) &&
          data.data.length
      );

      await sendXLSXAnalyticsReport(widgetsExportData, recipients);
      handleModalClose(null);
      successToast({ title: t('You have successfully sent a report') });
    } catch (error) {
      //TO_DO -> remove old toast and better error handling
      showError(error);
    }
    setLoading(false);
  };

  return (
    <Modal>
      <div ref={ref} style={{ padding: '32px' }}>
        <Header>
          <HeaderTitle>{t('Send Report')}</HeaderTitle>
          <CloseButtonWrap title={t('Close')}>
            <IoMdClose
              size={24}
              onClick={() => handleModalClose(null)}
              color={themes.colors.neutral[60]}
            />
          </CloseButtonWrap>
        </Header>

        <Content>
          {loading ? (
            <LoadingIndicator isLoading={loading} height='50px' />
          ) : (
            <>
              <InfoMsgWrapper>
                <MdInfo color={getSecondaryColor(themes, 40)} size={20} />
                <Paragraph>
                  {t('The exported file will be sent to {{email}}.', {
                    email: userData.email,
                  })}
                </Paragraph>
              </InfoMsgWrapper>
              <Caption>
                {t('Share the report with additional recipients')}
              </Caption>
              <RecipientsSelector
                onChange={recipients => setRecipients(recipients)}
                onError={email => {
                  if (email) {
                    setError(t('Invalid email address.'));
                  } else {
                    setError('');
                  }
                }}
                showIcon
              />
              {error && <ErrorMessage>{error}</ErrorMessage>}
            </>
          )}
        </Content>
        <Footer>
          <Button
            variant='primary'
            text={loading ? t('Sending') : t('Generate and Send')}
            disabled={loading}
            onClick={sendReport}
          />
        </Footer>
      </div>
    </Modal>
  );
};
